import React from 'react';

import { ResponsiveActionMenu } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, Stack } from '@lyearn/ui';
import { Link as RouterLink } from '@/components/Link';
import WorkspaceAccessControlDialog from '@/components/WorkspaceAccessControl/components/WorkspaceAccessControlDialog';
import stopPropagation from '@/consts/stopPropogation';
import * as AdminEntity from '@/modules/corporate/modules/admin/components/AdminEntity';
import { WorkspaceActions } from '@/types/schema';

import { ListItemProps } from '../types';
import { listItemRootStyles } from './style';

const OVERRIDES = {
  PopoverButton: {
    classes: {
      open: 'opacity-100',
      closed: 'opacity-0 group-hover:opacity-100 focus-visible:opacity-100 xs:opacity-100',
    },
  },
};

function ListItem(props: ListItemProps) {
  const { url, actionMenu, children, statusLabel, classes, workspaceSubject, onClick } = props;
  const isClickable = Boolean(url || onClick);

  const ListContent = (
    <Stack
      className={cn(
        'group rounded-16 px-8 xs:px-0 xs:hover:bg-bg-primary',
        { 'hover:bg-bg-secondary': isClickable },
        { [listItemRootStyles]: isClickable },
        classes?.root,
      )}>
      <Stack
        className={cn(
          'admin-list-item w-full border-b border-divider',
          classes?.itemContainer ?? 'py-20 ps-8 xs:py-24 xs:ps-0',
        )}
        justifyContent="between">
        {children}

        <Stack alignItems="center" className="flex-shrink-0">
          {statusLabel}
          {actionMenu ? (
            <Box
              className="flex h-40 items-center ms-4"
              data-testid="action-menu-button"
              onClick={stopPropagation}>
              <ResponsiveActionMenu {...actionMenu} overrides={OVERRIDES}>
                <AdminEntity.ActionButton />
              </ResponsiveActionMenu>
            </Box>
          ) : null}
        </Stack>
      </Stack>
    </Stack>
  );

  let ResolvedListContent: JSX.Element = ListContent;

  if (url) {
    ResolvedListContent = <RouterLink to={url}>{ListContent}</RouterLink>;
  }

  if (onClick) {
    ResolvedListContent = (
      <Box className="cursor-pointer" role="button" tabIndex={0} onClick={onClick}>
        {ListContent}
      </Box>
    );
  }

  if (!workspaceSubject) {
    return ResolvedListContent;
  }

  return (
    <WorkspaceAccessControlDialog
      action={WorkspaceActions.Read}
      actionVerb="view"
      fallback={ListContent}
      subject={workspaceSubject}>
      {ResolvedListContent}
    </WorkspaceAccessControlDialog>
  );
}

export default ListItem;
